import { EventEmitter } from "events"; // dependency, not a node.js module

/**
 * A TimeFrame is a snapshot of data at a given time. The time is most likely a timestamp (ms)
 * and the data could be anything.
 */
export type TimeFrame<T> = {
  time: number;
  data: T;
};

/**
 * Fired as "tick" event by {@link TimeFrameAnimation} and derived classes
 * with the timestamp of the currently displayed state.
 *
 * @event
 */
export type TickEvent = {
  /**
   * Current time
   */
  time: number;
};

/**
 * An instance of TimeFrameAnimation provides methods and containers to manage animation
 * of provided TimeFrames
 * Fires {@link TickEvent}
 */
class TimeFrameAnimation<T> extends EventEmitter {
  private frames: TimeFrame<T>[] = [];
  private time = 0;
  private animationSpeed = 0;
  private lastTickTime = 0;

  /**
   * Add a new TimeFrame information to the animation. If the frame container is not empty,
   * then the provided TimeFrame data will be added at the corresct position based on its time.
   * @param time
   * @param data
   */
  protected addFrame(time: number, data: T) {
    const position = this.findSmallerFrameIndex(time);
    if (position < 0) {
      this.frames.unshift({
        time: time,
        data: data,
      });
    } else if (this.frames[position].time === time) {
      throw new Error("Frame with this time already exists");
    } else {
      this.frames.splice(position + 1, 0, {
        time: time,
        data: data,
      });
    }
    this.clampAnimation();
  }

  /**
   * Remove a frame using its time as an ID
   * @param time
   * @returns
   */
  protected removeFrame(time: number): TimeFrame<T>[] {
    const removed = this.frames.filter((frame) => frame.time === time);
    this.frames = this.frames.filter((frame) => frame.time !== time);
    this.clampAnimation();

    return removed;
  }

  /**
   * Call a function for each TimeFrame of the animation
   * @param action
   */
  protected forEachFrame(action: (frame: TimeFrame<T>) => void) {
    for (const frame of this.frames) {
      action(frame);
    }
  }

  /**
   * Get the time of the first TimeFrame (always the begining of the animation).
   * If the frame container is empty, returns Infinity
   */
  getAnimationStart() {
    return this.frames.length ? this.frames[0].time : Number.POSITIVE_INFINITY;
  }

  /**
   * Get the animation start as a Date object
   * @returns
   */
  getAnimationStartDate(): Date {
    return new Date(this.getAnimationStart() * 1000);
  }

  /**
   * Get the end time of the animation or -Infinity if empty.
   */
  getAnimationEnd() {
    return this.frames.length ? this.frames[this.frames.length - 1].time : Number.NEGATIVE_INFINITY;
  }

  /**
   * Get the animation end as a date object
   * @returns
   */
  getAnimationEndDate(): Date {
    return new Date(this.getAnimationEnd() * 1000);
  }

  /**
   * Get the current time of the animation
   */
  getAnimationTime() {
    return this.time;
  }

  /**
   * Get the current time of the animation as a Date object
   * @returns
   */
  getAnimationTimeDate() {
    return new Date(this.getAnimationTime() * 1000);
  }

  /**
   * Change the visualization to a specific time. Does not stop animation.
   */
  setAnimationTime(time: number) {
    this.time = time;
    this.clampAnimation();

    this.emit("animationTimeSet", <TickEvent>{
      time: this.time,
    });
  }

  private clampAnimation() {
    this.time = Math.max(this.getAnimationStart(), Math.min(this.getAnimationEnd(), this.time));
  }

  /**
   * Animate by a factor of real life speed.
   * Exampe, if `factor` is `10`, then the animation will play at 10 times the real life speed.
   * @param factor
   */
  animateByFactor(factor: number) {
    this.animate(factor);
  }

  /**
   * Changes the speed of the animation. `0` to stop.
   * The speed is in number of real world milliseconds per animation second.
   * Example: if timePerSecond is set to 10*1000, then the animation will run
   * 10x of real world speed.
   */
  animate(timePerSecond: number) {
    if (timePerSecond > 0 && this.animationSpeed === 0) {
      // the animation is starting (and it was paused before that)
      this.emit("playAnimation", <TickEvent>{
        time: this.time,
      });
    } else if (timePerSecond === 0 && this.animationSpeed > 0) {
      // the animation is paused (and it was playing befre that)
      this.emit("pauseAnimation", <TickEvent>{
        time: this.time,
      });
    }

    this.animationSpeed = timePerSecond;
    if (this.animationSpeed !== 0) {
      this.lastTickTime = performance.now();
    }
  }

  /**
   * Get the speed of the animation.
   * The speed is in number of real world seconds per animation second.
   */
  getAnimationSpeed(): number {
    return this.animationSpeed;
  }

  /**
   * Tells whether the animation is currently playing
   * @returns
   */
  isPlaying(): boolean {
    return this.animationSpeed > 0;
  }

  /**
   * Make the animation time progress based on the current timestamps.
   */
  protected animationTick() {
    const now = performance.now();
    if (this.animationSpeed > 0 && this.lastTickTime) {
      const step = now - this.lastTickTime;

      this.time += this.animationSpeed * (step / 1000);

      const start = this.getAnimationStart();
      const end = this.getAnimationEnd();
      this.time = start + ((this.time - start) % (end - start));
      if (Number.isNaN(this.time)) {
        this.time = start;
      }

      this.emit("tick", <TickEvent>{
        time: this.time,
      });
    }
    this.lastTickTime = now;
  }

  /**
   * Find the index of TimeFrame whose time is just immediately below targetTime
   * @param targetTime
   * @returns
   */
  private findSmallerFrameIndex(targetTime: number) {
    for (let i = this.frames.length - 1; i >= 0; --i) {
      if (this.frames[i].time <= targetTime) {
        return i;
      }
    }
    return -1;
  }

  /**
   * Based on the current animation time, retrieve the frame immediately before (frameA),
   * the frame immediately after (frameB) and the mix.
   * The mixe value is in the interval [0, 1], where close to `0` means the current time
   * is close to frameA and close to `1` means the current time is close to frameB. The mix
   * value is provided so that linear interpolation of data can be performed.
   * @returns
   */
  protected getCurrentFrames(): {
    frameA: TimeFrame<T> | null;
    frameB: TimeFrame<T> | null;
    mix: number;
  } {
    if (this.frames.length === 0) {
      return {
        frameA: null,
        frameB: null,
        mix: 0,
      };
    }

    let position = this.findSmallerFrameIndex(this.time);

    if (position < 0) {
      position = 0;
    }

    const frameA = this.frames[position];
    const frameB = this.frames[Math.min(this.frames.length - 1, position + 1)];

    const interframeDuration = frameB.time - frameA.time;
    const mix = interframeDuration === 0 ? 0 : (this.time - frameA.time) / interframeDuration;

    return {
      frameA,
      frameB,
      mix,
    };
  }

  /**
   * Providing a TimeFrame (time + data), get the TimeFrame from the annimation that is
   * directly after (when `direction` is positive) or immediately before (when `direction` is negative)
   * @param frame
   * @param direction
   * @returns
   */
  protected getNextFrame(frame: TimeFrame<T>, direction: number): TimeFrame<T> | null {
    const position = this.frames.findIndex((element) => element === frame);
    if (position < 0) return null;
    if (direction < 0) {
      return position > 0 ? this.frames[position - 1] : null;
    }

    return position < this.frames.length - 1 ? this.frames[position + 1] : null;
  }
}

export { TimeFrameAnimation };
